/**
 * @file wms-warehouse-kind.ts
 * @description Реестр видов складов (phase19.14, inventory.md W8).
 *
 * Справочник — данными, а не каскадом `if`: новый вид склада = запись в
 * `WAREHOUSE_KIND_META`, а не ветка в форме и не второй список опций
 * (docs/processes/engineering-principles.md, OCP). Единственный список
 * значений — `WAREHOUSE_KINDS`, чтобы select и подписи не разъехались.
 */

export type WarehouseKind =
  | "FINISHED_GOODS"
  | "RAW_MATERIALS"
  | "QUARANTINE"
  | "PICKUP_POINT"
  | "TRANSIT";

interface WarehouseKindMeta {
  /** Подпись в select и в списке складов. */
  label: string;
  /** Подсказка под полем — что это за склад. */
  hint: string;
  /**
   * true — остатки такого склада никогда не попадают в «доступно».
   * Сервер снимает `isSellable` принудительно (`resolveSellable`), UI
   * зеркалит поведение, чтобы флаг не «моргал» после сохранения.
   */
  neverSellable: boolean;
  /** false — адресного хранения (ячеек) у такого склада не бывает. */
  supportsBins: boolean;
}

export const WAREHOUSE_KIND_META: Record<WarehouseKind, WarehouseKindMeta> = {
  FINISHED_GOODS: {
    label: "Готовая продукция",
    hint: "Собранные и готовые к продаже товары. Источник продаж по умолчанию.",
    neverSellable: false,
    supportsBins: true,
  },
  RAW_MATERIALS: {
    label: "Сырьё и материалы",
    hint: "Травы, упаковка, компоненты. Продаются только после сборки.",
    neverSellable: false,
    supportsBins: true,
  },
  QUARANTINE: {
    label: "Карантин / брак",
    hint: "Брак, возвраты и товар на проверке. В продажу не попадает.",
    neverSellable: true,
    supportsBins: true,
  },
  PICKUP_POINT: {
    label: "Точка выдачи",
    hint: "Пункт самовывоза: товар уже уехал к клиенту.",
    neverSellable: false,
    supportsBins: false,
  },
  TRANSIT: {
    label: "Транзит",
    hint: "Товар в пути между складами. В продажу не попадает.",
    neverSellable: true,
    supportsBins: false,
  },
};

/** Порядок в select — от самого частого к редкому. */
export const WAREHOUSE_KINDS: WarehouseKind[] = [
  "FINISHED_GOODS",
  "RAW_MATERIALS",
  "QUARANTINE",
  "PICKUP_POINT",
  "TRANSIT",
];

/** Подпись вида склада; неизвестный код показываем как есть. */
export function warehouseKindLabel(kind: WarehouseKind | null | undefined): string {
  if (!kind) return WAREHOUSE_KIND_META.FINISHED_GOODS.label;
  return WAREHOUSE_KIND_META[kind]?.label ?? kind;
}

/**
 * Итоговое значение «доступен для продажи» с учётом вида склада — зеркало
 * серверного `resolveSellable`: у карантина и транзита флаг всегда снят.
 */
export function resolveSellable(kind: WarehouseKind, requested: boolean): boolean {
  return WAREHOUSE_KIND_META[kind]?.neverSellable ? false : requested;
}
